import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Tooltip,
  IconButton
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AuthContext from '../contexts/AuthContext';

export default function FormTemplates() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const { tokens } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplates = async () => {
    if (!tokens) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/form-templates`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch form templates');
      const data = await res.json();
      setTemplates(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [tokens]);

  const handleOpen = async (templateId) => {
    if (!tokens) return;
    try {
      const res = await fetch(`${apiBase}/form-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Container disableGutters sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }
  if (error) {
    return (
      <Container disableGutters>
        <Typography color="error" variant="h6" sx={{ mt: 4 }}>
          Error: {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Form Templates
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Filename</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={t.id} arrow>
                      <Typography variant="body2" sx={{ cursor: 'default' }}>
                        {t.id.split('-')[0]}
                      </Typography>
                    </Tooltip>
                    <Tooltip title="Copy ID" arrow>
                      <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => navigator.clipboard.writeText(t.id)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {t.name}
                    <Tooltip title="Open PDF" arrow>
                    <IconButton size="small" sx={{ ml: 1 }} onClick={() => handleOpen(t.id)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>{t.filename}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}